import React from "react";
import Icon, { type IconName } from "../../../components/AppIcon";
import type { ChangeType, KPICard as KPICardType } from "../../../types";

interface KPICardProps {
	title: string;
	value: string | number;
	change: string;
	changeType: ChangeType;
	icon: IconName;
	color?: KPICardType["color"];
}

const KPICard: React.FC<KPICardProps> = ({
	title,
	value,
	change,
	changeType,
	icon,
	color = "primary",
}) => {
	const getColorClasses = (colorType: KPICardType["color"]): string => {
		const colors = {
			primary: "bg-primary/10 text-primary",
			secondary: "bg-secondary/10 text-secondary",
			success: "bg-success/10 text-success",
			warning: "bg-warning/10 text-warning",
			error: "bg-error/10 text-error",
		};
		return colors[colorType] || colors.primary;
	};

	const getChangeColor = (type: ChangeType): string => {
		return type === "increase" ? "text-success" : "text-error";
	};

	return (
		<div className="bg-card border border-border rounded-lg p-4 sm:p-5 lg:p-6 card-shadow overflow-hidden">
			<div className="flex items-center justify-between mb-3 gap-2">
				<div className={`p-2 sm:p-2.5 lg:p-3 rounded-lg flex-shrink-0 ${getColorClasses(color)}`}>
					<Icon name={icon} size={18} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
				</div>
				{change && (
					<div
						className={`flex items-center space-x-1 flex-shrink-0 ${getChangeColor(
							changeType,
						)}`}
					>
						<Icon
							name={changeType === "increase" ? "TrendingUp" : "TrendingDown"}
							size={14}
							className="sm:w-4 sm:h-4"
						/>
						<span className="text-xs sm:text-sm font-medium whitespace-nowrap">{change}</span>
					</div>
				)}
			</div>
			<div className="min-w-0">
				<h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground mb-1 truncate">{value}</h3>
				<p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
			</div>
		</div>
	);
};

export default KPICard;
